/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  SimpleGrid,
  Text,
  VStack,
  Portal,
  Icon,
} from "@chakra-ui/react";
import axios from "axios";
import { format, isAfter, isBefore, isEqual } from "date-fns";
import { useSession } from "next-auth/client";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useMemo, useState } from "react";
import { Star, CheckSquare } from "react-feather";
import useSWR, { mutate } from "swr";
import { matchSorter } from "match-sorter";
import MobileSearch from "@/components/common/Table/MobileSearch";

import Calendar from "@/components/Calendar";
import Avatar from "@/components/common/Avatar";
import BoxCard from "@/components/common/BoxCard";
import Link from "@/components/common/Link";
import ParsedHTML from "@/components/common/ParsedHTML";
import Table from "@/components/common/Table";
import Excerpt from "@/components/News/Excerpt";
import { usePressOffice } from "@/hooks/usePress";
import { useToastPromise } from "@/hooks/useToast";
import {
  getAccreditationStatusColor,
  getAccreditationStatusIcon,
} from "@/utils/accreditation";
import useIsMobile from "@/hooks/useIsMobile";
import WithdrawAccreditationButton from "@/components/common/WithdrawAccreditationButton";
import { AccreditationTypes } from "@/types/accreditation";
import { getFileUri } from "@/utils/helpers";
import { Event } from "@/types/event";

type Props = {
  id: string | string[];
};

const OfficeProfile = ({ id }: Props): React.ReactElement => {
  const { t } = useTranslation("common");
  const toast = useToastPromise();
  const [mobileSearchValue, setMobileSearchValue] = useState("");
  const { data: office } = useSWR(id ? `/press-office/${id}` : null);
  const { data: officeEvents } = useSWR(id ? `/event/office/${id}` : null);
  const { articles: officeArticles } = usePressOffice(+id);
  const [loading, setLoading] = useState(false);
  const [session] = useSession();
  const router = useRouter();
  const isMobile = useIsMobile();
  const now = new Date();

  //TODO filter events on backend
  const officeEventsList = useMemo(() => {
    if (officeEvents?.length > 0)
      return officeEvents?.filter(
        (event: Event) =>
          isBefore(now, new Date(event?.endDate)) ||
          isEqual(now, new Date(event?.endDate))
      );
    return [];
  }, [officeEvents, now]);

  const filteredMobileData = useMemo(() => {
    if (!mobileSearchValue) return officeEventsList;
    return mobileSearchValue.split(" ").reduceRight(
      (results, term) =>
        matchSorter(results, term, {
          keys: ["name"],
        }),
      officeEventsList
    );
  }, [officeEventsList, mobileSearchValue]);

  const articles = useMemo(
    () => (officeArticles || []).slice(0, 5),
    [officeArticles]
  );

  React.useEffect(() => {
    if (!office || !router.query.subscribe) return;
    if (!office.subscribed) onSubscribeClick();
    router.push({ pathname: router.pathname, query: { id } });
  }, [office]);

  const onSubscribeClick = () => {
    setLoading(true);
    return toast.promise(
      axios
        .post(`/press-office/${id}/subscription`, {
          status: office?.subscribed ? "unsubscribe" : "subscribe",
        })
        .then(() => {
          mutate(`/press-office/${id}`);
          mutate("/press-office?latest=true");
        })
        .finally(() => setLoading(false)),
      {
        success: office?.subscribed
          ? t("pressOffice.unsubsribingPressOffice")
          : t("pressOffice.pressOfficeIsNowSubscribed"),
      }
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: t("events.event"),
        accessor: "name",
        Cell: ({ row }) => (
          <Flex align="center">
            <Popover trigger="hover" isLazy>
              <PopoverTrigger>
                <Avatar
                  src={
                    row.original?.picture
                      ? getFileUri(row.original?.picture)
                      : null
                  }
                  size="sm"
                  mr={4}
                  name={row.original?.name}
                />
              </PopoverTrigger>
              {row?.original?.description && (
                <Portal>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverHeader>
                      <Flex flex="1" alignItems="center">
                        <Avatar
                          src={
                            row.original?.picture
                              ? getFileUri(row.original?.picture)
                              : null
                          }
                          size="sm"
                          name={row.original?.name}
                        />
                        <Text ml={3} fontWeight="500">
                          {row.original?.name}
                        </Text>
                      </Flex>
                    </PopoverHeader>
                    <PopoverBody>
                      <Text textAlign={"justify"}>
                        <ParsedHTML
                          html={
                            row.original?.description[
                              session?.user?.profile?.locale
                            ] ??
                            row.original?.description.en ??
                            row.original?.description[
                              Object.keys(row.original?.description)[0]
                            ]
                          }
                        />
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              )}
            </Popover>
            <Text fontWeight="500">{row.original?.name}</Text>
          </Flex>
        ),
      },
      {
        Header: t("settings.start"),
        accessor: "startDate",
        Cell: ({ value }) => (
          <Text>{value ? format(new Date(value), "dd/MM/yyyy") : "-"}</Text>
        ),
      },
      {
        Header: t("settings.end"),
        accessor: "endDate",
        Cell: ({ value }) => (
          <Text>{value ? format(new Date(value), "dd/MM/yyyy") : "-"}</Text>
        ),
      },
      {
        id: "event",
        accessor: "id",
        Cell: ({ value, row }) => {
          const startAccreditation =
            row?.original?.accreditationFrom &&
            new Date(row.original.accreditationFrom);

          const endAccreditation =
            row?.original?.accreditationTo &&
            new Date(row.original.accreditationTo);

          const submittedAcreditation = row?.original?.accreditations?.find(
            (accreditation) => accreditation?.userId === session?.user?.id
          );

          return (
            <Flex flexDirection="column" alignItems="end" gap={2}>
              {submittedAcreditation ? (
                <>
                  <Button
                    bgColor={getAccreditationStatusColor(
                      submittedAcreditation?.status
                    )}
                    size="sm"
                    _hover={{
                      bgColor: getAccreditationStatusColor(
                        submittedAcreditation?.status
                      ),
                    }}
                    _focus={{
                      bgColor: getAccreditationStatusColor(
                        submittedAcreditation?.status
                      ),
                    }}
                    onClick={() => {
                      if (
                        submittedAcreditation?.status ===
                          AccreditationTypes.PENDING ||
                        submittedAcreditation?.status ===
                          AccreditationTypes.INCOMPLETE
                      )
                        router.push(`/accreditation/event/${value}`);
                      else return null;
                    }}
                  >
                    <Icon
                      as={getAccreditationStatusIcon(
                        submittedAcreditation?.status
                      )}
                      w={5}
                      h={5}
                      mr={2}
                    />
                    {t(`events.statusInfo.${submittedAcreditation?.status}`)}
                  </Button>
                  <WithdrawAccreditationButton
                    status={submittedAcreditation?.status}
                    accreditationId={submittedAcreditation?.id}
                    eventId={value}
                    userId={submittedAcreditation?.userId}
                    size="sm"
                    variant="outline"
                  />
                </>
              ) : (
                <>
                  {!row?.original?.accreditationActive &&
                    startAccreditation && (
                      <Text
                        color="text.500"
                        fontSize="xs"
                        mt={1}
                        textAlign="left"
                      >
                        {isAfter(now, startAccreditation)
                          ? t("events.accreditationFromEnded")
                          : t("events.accreditationFrom", {
                              date: format(
                                startAccreditation,
                                "dd/MM/yyyy HH:mm"
                              ),
                            })}
                      </Text>
                    )}

                  {!row?.original?.accreditationActive &&
                    !startAccreditation && (
                      <Text
                        color="text.500"
                        fontSize="xs"
                        mt={1}
                        textAlign="left"
                      >
                        {t("events.accreditationNotStarted")}
                      </Text>
                    )}
                  {row?.original?.accreditationActive && endAccreditation && (
                    <>
                      <Button
                        size="sm"
                        onClick={() =>
                          router.push(`/accreditation/event/${value}`)
                        }
                      >
                        <Icon as={CheckSquare} w={5} h={5} mr={2} />
                        {t("events.accredit")}
                      </Button>
                      <Text
                        color="text.500"
                        fontSize="xs"
                        mt={1}
                        textAlign="left"
                      >
                        {t("events.accreditationTo", {
                          date: format(endAccreditation, "dd/MM/yyyy HH:mm"),
                        })}
                      </Text>
                    </>
                  )}
                </>
              )}
            </Flex>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <Flex
        justifyContent={"space-between"}
        align={"center"}
        mb="1.5rem"
        alignItems={{ base: "flex-end", md: "center" }}
        direction={{ base: "column", md: "row" }}
      >
        <HStack flex={{ base: 1, md: "none" }}>
          <Avatar
            src={getFileUri(office?.picture)}
            name={office?.name}
            mr={3}
            size="lg"
          />
          <Heading as="h1" size="xl">
            {office?.name}
          </Heading>
        </HStack>
        <Flex justifyContent={"flex-end"} w={{ base: "100%", md: "auto" }}>
          <Button
            mt={{ base: 4, md: 0 }}
            rightIcon={
              <Star
                color={office?.subscribed ? "text.400" : undefined}
                fill={office?.subscribed ? "text.400" : "none"}
              />
            }
            size="lg"
            variant={office?.subscribed ? "ghost" : undefined}
            onClick={onSubscribeClick}
            isLoading={loading}
          >
            {office?.subscribed
              ? t("website.unsubscribe")
              : t("website.subscribe")}
          </Button>
        </Flex>
      </Flex>
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={5}>
        <GridItem order={{ base: 2, md: 1 }}>
          <Box>
            <Heading as="h2" size="lg">
              {t("press.news")}
            </Heading>
            {officeArticles?.length > 0 ? (
              <SimpleGrid columns={1} spacing={6}>
                {articles.map((article) => (
                  <Excerpt key={`article-${article.id}`} article={article} />
                ))}
              </SimpleGrid>
            ) : (
              <BoxCard p={3} textAlign="center">
                <Text fontWeight="500">{t("press.noArticles")}</Text>
              </BoxCard>
            )}
          </Box>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }} order={{ base: 1, md: 2 }}>
          <VStack spacing="1.5rem">
            <Box w="100%">
              <Heading as="h2" size="lg">
                {t("events.pressOfficeEvents")}
              </Heading>
              <BoxCard p={0} w="100%">
                {officeEventsList?.length > 0 ? (
                  <>
                    {isMobile ? (
                      <Box>
                        <MobileSearch
                          searchValue={mobileSearchValue}
                          onSearchChange={setMobileSearchValue}
                          count={filteredMobileData?.length}
                        />
                        <SimpleGrid columns={1} spacing={3}>
                          {filteredMobileData?.map((row) => {
                            const startAccreditation =
                              row?.accreditationFrom &&
                              new Date(row?.accreditationFrom);

                            return (
                              <BoxCard p={0} key={row?.id}>
                                <Flex
                                  align="center"
                                  justify="space-between"
                                  p="1.25rem"
                                  borderBottomColor="light.50"
                                  borderBottomWidth="1px"
                                >
                                  <Flex align="center">
                                    <Popover trigger="hover" isLazy>
                                      <PopoverTrigger>
                                        <Avatar
                                          name={row?.name}
                                          size="md"
                                          mr={3}
                                          src={getFileUri(row?.picture)}
                                        />
                                      </PopoverTrigger>
                                      {row?.description && (
                                        <Portal>
                                          <PopoverContent>
                                            <PopoverArrow />
                                            <PopoverHeader>
                                              <Flex
                                                flex="1"
                                                alignItems="center"
                                              >
                                                <Avatar
                                                  src={
                                                    row?.picture
                                                      ? getFileUri(row?.picture)
                                                      : null
                                                  }
                                                  size="sm"
                                                  name={row?.name}
                                                />
                                                <Text ml={3} fontWeight="500">
                                                  {row?.name}
                                                </Text>
                                              </Flex>
                                            </PopoverHeader>
                                            <PopoverBody>
                                              <Text textAlign={"justify"}>
                                                <ParsedHTML
                                                  html={
                                                    row?.description[
                                                      session?.user?.profile
                                                        ?.locale
                                                    ] ??
                                                    row?.description.en ??
                                                    row?.description[
                                                      Object.keys(
                                                        row?.description
                                                      )[0]
                                                    ]
                                                  }
                                                />
                                              </Text>
                                            </PopoverBody>
                                          </PopoverContent>
                                        </Portal>
                                      )}
                                    </Popover>
                                    <Box>
                                      <Text fontWeight="500">{row.name}</Text>
                                      <Text color="text.500" fontSize="xs">
                                        {row?.startDate
                                          ? format(
                                              new Date(row?.startDate),
                                              "dd/MM/yyyy"
                                            )
                                          : ""}
                                        &nbsp;-&nbsp;
                                        {row?.endDate
                                          ? format(
                                              new Date(row?.endDate),
                                              "dd/MM/yyyy"
                                            )
                                          : ""}
                                      </Text>
                                    </Box>
                                  </Flex>
                                </Flex>
                                {!row?.accreditationActive &&
                                !startAccreditation ? (
                                  <Text
                                    color="text.500"
                                    fontSize="sm"
                                    textAlign="center"
                                    pb={2}
                                  >
                                    {t("events.accreditationNotStarted")}
                                  </Text>
                                ) : (
                                  <Flex
                                    p="1.25rem"
                                    justifyContent="flex-end"
                                    fontSize="20px"
                                    w="100%"
                                  >
                                    <Link to={`/accreditation/event/${row.id}`}>
                                      <Button size="sm">
                                        {t("events.accredit")}
                                      </Button>
                                    </Link>
                                  </Flex>
                                )}
                              </BoxCard>
                            );
                          })}
                        </SimpleGrid>
                      </Box>
                    ) : (
                      <Box pl={1} pr={1}>
                        <Table
                          columns={columns}
                          data={officeEventsList || []}
                          searchBar={false}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <BoxCard p={3} textAlign="center">
                    <Text fontWeight="500">{t("events.noEvents")}</Text>
                  </BoxCard>
                )}
              </BoxCard>
            </Box>
            {!isMobile && (
              <Box w="100%">
                <Heading as="h3" size="lg">
                  {t("calendar.calendar")}
                </Heading>
                <div className="dashboard">
                  <Calendar small customEvents={officeEvents} />
                </div>
              </Box>
            )}
          </VStack>
        </GridItem>
      </Grid>
    </>
  );
};

export default OfficeProfile;
